import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { Title } from "components/Common/Title"
import { ExamSectionSkeleton } from "./ExamSectionSkeleton"
import { hiabColors } from "theme"
import analyticsService from "services/analyticsService"
import type { IAnalyticsExamsEquipment } from "types/analyticsTypes"
import { applyPassRateFilter } from "./analyticsFilters"
import type { PassRateFilterOption } from "./analyticsFilters"

type FilterOption = PassRateFilterOption

type Props = {
  orgId: string | null
}

export function ExamEquipmentSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamsEquipment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterOption>("bottom10")

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    analyticsService
      .getExamsEquipment(orgId)
      .then((result) => {
        const sorted = [...result].sort((a, b) => a.passRate - b.passRate)
        setData(sorted)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load equipment data.")
        setLoading(false)
      })
  }, [orgId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = (event: SelectChangeEvent<FilterOption>) => {
    setFilter(event.target.value as FilterOption)
  }

  if (loading) return <ExamSectionSkeleton rows={5} />

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>PASS RATES BY LESSON AND EQUIPMENT</Title>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={fetchData} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    )
  }

  if (data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>PASS RATES BY LESSON AND EQUIPMENT</Title>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No equipment exam data available.
        </Typography>
      </Paper>
    )
  }

  const displayData = applyPassRateFilter(data, filter)

  return (
    <Paper sx={{ p: 3 }}>
      <Title>PASS RATES BY LESSON AND EQUIPMENT</Title>
      <Box sx={{ mb: 1, mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Show</InputLabel>
          <Select<FilterOption> value={filter} label="Show" onChange={handleFilterChange}>
            <MenuItem value="bottom10">Bottom 10</MenuItem>
            <MenuItem value="top10">Top 10</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Crane</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Tool</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Taken</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Passed</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Pass Rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((row) => (
              <TableRow key={`${row.lessonId}-${row.crane}-${row.tool}`}>
                <TableCell>{row.lessonName ?? `Unknown (${row.lessonId})`}</TableCell>
                <TableCell>{row.crane}</TableCell>
                <TableCell>{row.tool}</TableCell>
                <TableCell align="right">{row.examsTaken}</TableCell>
                <TableCell align="right">{row.examsPassed}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: row.passRate < 50 ? hiabColors.redPrimary : "inherit",
                    fontWeight: row.passRate < 50 ? 600 : "inherit",
                  }}
                >
                  {row.passRate}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
